import {makeAutoObservable, runInAction} from 'mobx';
import {
    networkOptimization,
    streamPersistence
} from '../../../lib/api';
import {
    GeographyFilterOptions,
    FetchGeoJsonResponse,
    FetchComplianceResponse,
    FetchProvidersResponse,
    FetchRunIdResponse,
    AppliedGeographyFilter,
    DISTANCE_CALC_TYPES,
    DisruptionAnalysisFilterOptions,
    AppliedDisruptionAnalysisFilter,
    IFetchReportsPayload,
    IGeographyFilterPayload,
    IDisruptionAnalysisFilterPayload,
    UploadJob,
    ProcessingFile,
    FileProcessingEvent,
    FileEvent,
    NavItem
} from './types';
import {IProcessingFilesService, processingFilesService} from './services/processing-files-service';
import {NotificationStore} from './stores/notification-store';
import {PatientStore} from './stores/patient-store';
import {ProviderStore} from './stores/provider-store';
import {GeographyStore} from './stores/geography-store';
import {DisruptionStore} from './stores/disruption-store';
import {RosterStore} from './stores/roster-store';
import {RosterComparisonStore} from './stores/roster-comparison-store';
import {ReportStore} from './stores/report-store';
import {FileUploadStore} from './stores/file-upload-store';
import {FileListStore} from './stores/file-list-store';
import {SSEConnectionStore} from './stores/sse-connection-store';
import {UIStateStore} from './stores/ui-state-store';
import Filter, {applyFilter} from '../../../lib/filter';
import {ParsedApiError, ErrorDomain, parseApiError} from '../../../lib/error-handler';
import {isAbortError} from '../../../lib/error-status';
export default class NetworkOptimizationStore {

    static empty() {
        return new NetworkOptimizationStore();
    }

    // Store properties
    private _multiSpecialtyProviderGeoJson: Record<string, FetchGeoJsonResponse> | null = null;
    private _distanceCalcType: string = DISTANCE_CALC_TYPES[0].value;
    private _selectedRosterForComparison = '';

    get patientGeoJson() { return this.patientStore.geoJson; }
    set patientGeoJson(value: FetchGeoJsonResponse | null) { this.patientStore.geoJson = value; }

    get providerGeoJson(): FetchGeoJsonResponse | null {
        return this.providerStore.geoJson ? {...this.providerStore.geoJson, features: this.providerStore.geoJson.features.filter((f: any) => !this.deselectedExistingProviderKeys.some(p => p === f.properties!.provider_address_fact_key))} : this.providerStore.geoJson;
    }

    set providerGeoJson(value: FetchGeoJsonResponse | null) { this.providerStore.geoJson = value; }

    get localProviders(): FetchProvidersResponse {
        const deselectedExisting = this.providerStore.existingProviders.filter((p: any) => this.deselectedExistingProviderKeys.some(s => s === p['id']));
        return this.filterProviderList(this.providerStore.localProviders).concat(deselectedExisting);
    }

    get existingProviders() {
        return this.providerStore.existingProviders.filter((p: any) => !this.deselectedExistingProviderKeys.some(s => s === p['id']));
    }

    get additionalProviderVisibleColumns(): string[] { return this.uiStateStore.additionalProviderVisibleColumns; }
    get additionalProviderPendingColumns(): string[] { return this.uiStateStore.additionalProviderPendingColumns; }
    get selectedAdditionalProviderKeys(): string[] { return this.providerStore.selectedAdditionalKeys; }
    get deselectedExistingProviderKeys(): string[] { return this.providerStore.deselectedExistingKeys; }
    get existingProviderQueuedForDeletion(): string | null { return this.uiStateStore.existingProviderQueuedForDeletion; }
    set existingProviderQueuedForDeletion(value: string | null) { this.uiStateStore.setExistingProviderQueuedForDeletion(value); }

    get appliedGeographyFilter(): AppliedGeographyFilter { return this.geographyStore.appliedGeographyFilter; }
    set appliedGeographyFilter(value: AppliedGeographyFilter) { this.geographyStore.appliedGeographyFilter = value; }
    get availableGeographyFilters(): Array<GeographyFilterOptions> { return this.geographyStore.availableGeographyFilters; }
    get specialtyFilter(): string { return this.geographyStore.specialtyFilter; }
    set specialtyFilter(value: string) { this.geographyStore.setSpecialtyFilter(value); }
    get specialties(): string[] { return this.geographyStore.specialties; }
    get loadingGeographyData(): boolean { return this.geographyStore.loadingGeographyData; }
    get loadingSpecialtyData(): boolean { return this.geographyStore.loadingSpecialtyData; }

    // Multi-select specialty filter methods
    get maxSpecialtySelection(): number { return this.geographyStore.maxSpecialtySelection; }
    toggleSpecialtyFilter(specialtyCode: string): void { this.geographyStore.toggleSpecialtyFilter(specialtyCode); }
    clearSpecialtyFilters(): void { this.geographyStore.clearSpecialtyFilters(); }
    isSpecialtySelected(specialtyCode: string): boolean { return this.geographyStore.isSpecialtySelected(specialtyCode); }
    canSelectMoreSpecialties(): boolean { return this.geographyStore.canSelectMoreSpecialties(); }
    getSelectedSpecialtyCount(): number { return this.geographyStore.getSelectedSpecialtyCount(); }

    get appliedDisruptionAnalysisFilter(): AppliedDisruptionAnalysisFilter { return this.disruptionStore.appliedDisruptionAnalysisFilter; }
    set appliedDisruptionAnalysisFilter(value: AppliedDisruptionAnalysisFilter) { this.disruptionStore.appliedDisruptionAnalysisFilter = value; }
    get availableDisruptionAnalysisFilters(): Array<DisruptionAnalysisFilterOptions> { return this.disruptionStore.availableDisruptionAnalysisFilters; }
    get disruptionAnalysisSpecialtyFilter(): string { return this.disruptionStore.disruptionAnalysisSpecialtyFilter; }
    set disruptionAnalysisSpecialtyFilter(value: string) { this.disruptionStore.setDisruptionAnalysisSpecialtyFilter(value); }
    get rosterComparisonDisruptionAnalysisData() { return this.disruptionStore.rosterComparisonDisruptionAnalysisData; }
    get loadingDisruptionAnalysisData(): boolean { return this.disruptionStore.loadingDisruptionAnalysisData; }
    get loadingDisruptionAnalysisComparisonData(): boolean { return this.disruptionStore.loadingDisruptionAnalysisComparisonData; }
    get loadingDisruptionAnalysisSpecialtyData(): boolean { return this.disruptionStore.loadingDisruptionAnalysisSpecialtyData; }

    get loadingRevisionRecalculation(): boolean { return this.rosterStore.loadingRevisionRecalculation; }
    get isRosterActionLoading(): boolean { return this.rosterStore.isRosterActionLoading; }
    get rosterRunCount(): number { return this.rosterStore.rosterRunCount; }

    loadingStandardVersions = false;
    runId: FetchRunIdResponse = null;
    errorsByDomain: Map<ErrorDomain, ParsedApiError> = new Map();

    revisionId = 0;
    rosterFileName = '';
    filters: Filter[] = [];

    // Notification queue - delegate to NotificationStore
    get notifications() { return this.notificationStore.notifications; }

    // Compliance scores - delegate to domain stores
    get complianceScores(): FetchComplianceResponse { return this.geographyStore.complianceScores; }
    get defaultComplianceScores(): FetchComplianceResponse { return this.geographyStore.defaultComplianceScores; }
    get disruptionAnalysisComplianceScores(): FetchComplianceResponse { return this.disruptionStore.disruptionAnalysisComplianceScores; }



    constructor(
        private filesService: IProcessingFilesService = processingFilesService,
        private notificationStore: NotificationStore = new NotificationStore(),
        private patientStore: PatientStore = new PatientStore(),
        private providerStore: ProviderStore = new ProviderStore(),
        public geographyStore: GeographyStore = new GeographyStore(),
        private disruptionStore: DisruptionStore = new DisruptionStore(),
        private reportStore: ReportStore = new ReportStore(),
        private sseConnectionStore: SSEConnectionStore = new SSEConnectionStore(),
        private fileUploadStore: FileUploadStore = new FileUploadStore(
            filesService,
            (kind: string, title: string, subtitle: string, options?: {errorId?: string; action?: {label: string; onClick: () => void}; timeout?: number}) => this.showToastNotification(kind as 'success' | 'error' | 'info' | 'warning', title, subtitle, options),
            networkOptimization,
            NetworkOptimizationStore.UPLOAD_CHUNK_SIZE,
            (fileId: string, status: string) => this.fileListStore.setProcessingFileStatus(fileId, status)
        ),
        private fileListStore: FileListStore = new FileListStore(
            filesService,
            sseConnectionStore,
            (kind: string, title: string, subtitle: string, options?: {errorId?: string; action?: {label: string; onClick: () => void}; timeout?: number}) => this.showToastNotification(kind as 'success' | 'error' | 'info' | 'warning', title, subtitle, options),
            networkOptimization,
            (error: unknown) => this.isAbortError(error)
        ),
        private rosterStore: RosterStore = new RosterStore(
            sseConnectionStore,
            (kind: string, title: string, subtitle: string) => this.showToastNotification(kind as 'success' | 'error' | 'info' | 'warning', title, subtitle),
            () => this.hideToastNotification()
        ),
        private _rosterComparisonStore: RosterComparisonStore = new RosterComparisonStore(
            (kind: string, title: string, subtitle: string) => this.showToastNotification(kind as 'success' | 'error' | 'info' | 'warning', title, subtitle),
            // Its own filter data, so the comparison loads the primary roster's filter options
            // without replacing the route roster's that Network Analysis shows
            new GeographyStore()
        ),
        private uiStateStore: UIStateStore = new UIStateStore()
    ) {
        this.loadHeaders();
        makeAutoObservable(this, {_uploadAbortControllers: false, _isRunning: false} as any);
    }

    /**
     * Check for existing SSE streams in sessionStorage and attempt to reconnect
     *
     * Called after runId and revisionId are set to restore active streams
     * after page refresh. Handles three stream types:
     * - recalculation: Network adequacy recalculation
     * - roster_copy: Roster copy operations
     * - file_upload: File processing status updates
     */
    checkForExistingStreams() {
        if (this.rosterStore.loadingRevisionRecalculation || this.rosterStore.isRosterActionLoading) {
            return;
        }

        streamPersistence.cleanupOldStreams();
        const streams = streamPersistence.getAllStreams();

        Object.keys(streams).forEach(streamId => {
            if (this.sseConnectionStore.isStreamReconnected(streamId)) {
                return;
            }

            const streamData = streams[streamId];

            if (streamData.streamType === 'recalculation') {
                if (streamData.runId === this.runId && streamData.revisionId === this.revisionId) {
                    this.sseConnectionStore.markStreamAsReconnected(streamId);
                    this.rosterStore.reconnectToRecalculationStream(streamId);
                }
            } else if (streamData.streamType === 'roster_copy') {
                this.sseConnectionStore.markStreamAsReconnected(streamId);
                this.rosterStore.reconnectToRosterCopyStream(
                    streamId,
                    streamData,
                    this.rosterStore.providerRosters,
                    (rosterId: number, revisionName: string, notes: string) => this.rosterStore.addNewRevisionToRoster(rosterId, revisionName, notes)
                );
            }
            // Note: file_upload streams are no longer reconnected on page refresh
            // User must reopen details panel to reconnect
        });
    }

    reset() {
        this.patientStore.geoJson = null;
        this.providerStore.geoJson = null;
        this._multiSpecialtyProviderGeoJson = null;
        this.reportStore.resetReportProviderGeoJson();
        this.appliedGeographyFilter = {
            state: '',
            county: '',
            patient_roster_name: '',
            standard_type: '',
            standard_version: ''
        };
        this._distanceCalcType = DISTANCE_CALC_TYPES[0].value;
        this.geographyStore.resetGeographyFilter();
        this.geographyStore.reset();
        this.disruptionStore.resetDisruptionAnalysisFilter();
        this.rosterStore.reset();
        this.providerStore.reset();
        this.loadingStandardVersions = false;
        this.disruptionStore.disruptionAnalysisComplianceScores = [];
        this.runId = null;
        this.errorsByDomain.clear();
        this.navItems = [];
        this.revisionId = 0;
        this.rosterFileName = '';
        this.uiStateStore.setExistingProviderQueuedForDeletion(null);
        this.showRevertModal = false;
        this.sseConnectionStore.clearReconnectedStreams();
    }

    async loadGeographyData() {
        if (!this.runId) {
            throw new Error('Cannot load geography data: runId not set');
        }
        const controller = new AbortController();
        await this.geographyStore.loadGeographyData(this.runId, controller);
        this.resetGeographyFilter();
    }

    async loadDisruptiveAnalysisFilterData(controller?: AbortController) {
        if (!this.runId) {
            throw new Error('Cannot load disruption analysis filters: runId not set');
        }
        const actualController = controller || new AbortController();
        await this.disruptionStore.loadDisruptiveAnalysisFilterData(this.runId, actualController);
        this.resetDisruptionAnalysisFilter();
    }

    getGeoFilterUniqueValuesByKey(
        filterType: 'GEOGRAPHY' | 'DISRUPTION_ANALYSIS',
        key: string | string[],
        filter?: Record<string, string>,
        separator?: string,
        sortOrder?: 'ASC' | 'DESC'
    ): string[] {
        return filterType === 'GEOGRAPHY'
            ? this.geographyStore.getUniqueValuesByKey(key, filter, separator, sortOrder)
            : this.disruptionStore.getUniqueValuesByKey(key, filter);
    }

    //fetchNavheaders
    async loadHeaders(controller?: AbortController) {
        try {
            await this.uiStateStore.loadHeaders(controller);
        } catch (error) {
            if (this.isAbortError(error)) return;
            throw error;
        }
    }

    // Fetch and update the map data
    async loadMapGeoJson(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            const baseFilters: Record<string, string> = {...this.geographyFilters};

            this.patientStore.geoJson = null;
            this.providerStore.geoJson = null;
            this._multiSpecialtyProviderGeoJson = null;

            if (this.selectedSpecialtyCount >= 1) {
                const codes = this.specialtyFilterCode.split('|').filter(Boolean);
                const batchFilters = {...baseFilters, specialty_code: codes};
                // Single specialty: patients filtered by specialty for compliance colors
                // Multi specialty: patients unfiltered (will be greyed on map)
                const patientServiceCall: any = this.selectedSpecialtyCount === 1
                    ? networkOptimization.fetchPatientGeoJson
                    : networkOptimization.fetchPatientPopulationGeoJson;
                const patientFilters = this.selectedSpecialtyCount === 1
                    ? {...baseFilters, specialty: this.specialtyFilterCode}
                    : baseFilters;
                const [patientData, providerData, batchResponse] = await Promise.all([
                    patientServiceCall(patientFilters, this.runId!, this.revisionId, this._distanceCalcType, controller.signal),
                    networkOptimization.fetchProviderGeoJson(baseFilters, this.runId!, this.revisionId, controller.signal),
                    networkOptimization.fetchProviderGeoJsonBatch(batchFilters, this.runId!, this.revisionId, controller.signal)
                ]);
                const batchData = (batchResponse as any).data ?? batchResponse;
                runInAction(() => {
                    this.patientStore.geoJson = patientData as FetchGeoJsonResponse;
                    this.providerStore.geoJson = providerData as FetchGeoJsonResponse;
                    this._multiSpecialtyProviderGeoJson = batchData as Record<string, FetchGeoJsonResponse>;
                });
            } else {
                const [patientData, providerData] = await Promise.all([
                    networkOptimization.fetchPatientPopulationGeoJson(baseFilters, this.runId!, this.revisionId, this._distanceCalcType, controller.signal),
                    networkOptimization.fetchProviderGeoJson(baseFilters, this.runId!, this.revisionId, controller.signal)
                ]) as [FetchGeoJsonResponse, FetchGeoJsonResponse];
                runInAction(() => {
                    this.patientStore.geoJson = patientData;
                    this.providerStore.geoJson = providerData;
                });
            }
        } catch (error) {
            const parsedError = parseApiError(error as Error);
            if (parsedError.isAbortError) return;
            throw error;
        }
    }


    // Fetch and update the disruption analysis map data
    async disruptionAnalysisLoadMapGeoJson(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        if (!this.runId) {
            throw new Error('Cannot load disruption analysis map data: runId not set');
        }
        let filters: Record<string, string> = {
            ...this.disruptionAnalysisFilters
        };
        if (this.disruptionAnalysisSpecialtyFilter)
            filters.specialty = this.disruptionAnalysisSpecialtyFilterCode;

        this.patientStore.geoJson = null;
        this.providerStore.geoJson = null;

        await Promise.all([
            this.patientStore.loadDisruptionAnalysisPatientGeoJson(
                filters,
                this.runId,
                this.revisionId,
                controller,
                !this.disruptionAnalysisSpecialtyFilter
            ),
            this.providerStore.loadDisruptionAnalysisProviderGeoJson(
                filters,
                this.runId,
                this.revisionId,
                controller
            )
        ]);
    }

    // Fetch and update the local providers list
    async loadLocalProviders(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        if (!this.runId) {
            throw new Error('Cannot load local providers: runId not set');
        }
        let filters: Record<string, string> = {
            ...this.geographyFilters
        };
        if (this.specialtyFilter)
            filters.specialty = this.specialtyFilterCode;

        return await this.providerStore.loadLocalProviders(
            filters,
            this.runId,
            this.revisionId,
            this._distanceCalcType,
            controller
        );
    }

    // Fetch and update the local disruption analysis providers list
    async loadDisruptionAnalysisLocalProviders(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        if (!this.runId) {
            throw new Error('Cannot load disruption analysis providers: runId not set');
        }
        let filters: Record<string, string> = {
            ...this.disruptionAnalysisFilters
        };
        if (this.disruptionAnalysisSpecialtyFilter)
            filters.specialty = this.disruptionAnalysisSpecialtyFilterCode;

        await this.providerStore.loadDisruptionAnalysisProviders(
            filters,
            this.runId,
            this.revisionId,
            controller
        );
    }

    async loadDisruptionAnalysisComparison(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        if (!this.runId) {
            throw new Error('Cannot load disruption analysis comparison: runId not set');
        }
        const selectedRostersStr = this.selectedRosters
            .map(r => `${r.rosterId}-${r.revisionId}`)
            .join(',');

        let filters: Record<string, string> = {
            ...this.disruptionAnalysisFilters,
            selectedRosters: selectedRostersStr,
            specialty: this.disruptionAnalysisSpecialtyFilterCode
        };
        delete filters.county;
        delete filters.zip;
        delete filters.patient_roster_id;
        delete filters.state;
        try {
            await this.disruptionStore.loadDisruptionAnalysisComparison(filters, this.runId, controller);
        } catch (error) {
            // Silently handle abort errors - component unmounted or navigation occurred
            if (error instanceof Error && isAbortError(error)) {
                return;
            }
            throw error;
        }
    }

    async loadSpecialtiesData(controller: AbortController, provider_facility_type = '', includeStateAndCounty = true) {
        if (!this.readyToLoadData()) return;
        if (!this.runId) {
            throw new Error('Cannot load specialties data: runId not set');
        }
        await this.geographyStore.loadSpecialtiesData(
            this.runId,
            this.revisionId,
            this._distanceCalcType,
            this.geographyStore.getGeographyFiltersPayload() as IGeographyFilterPayload,
            this.appliedGeographyFilter.state,
            this.appliedGeographyFilter.county,
            controller,
            provider_facility_type,
            includeStateAndCounty
        );
    }

    async loadDisruptionAnalysisSpecialtiesData(controller: AbortController) {
        if (!this.readyToLoadData()) return;
        if (!this.runId) {
            throw new Error('Cannot load disruption analysis specialties: runId not set');
        }
        await this.disruptionStore.loadDisruptionAnalysisSpecialtiesData(
            this.runId,
            this.revisionId,
            this.disruptionStore.getDisruptionAnalysisFiltersPayload() as IDisruptionAnalysisFilterPayload,
            this.disruptionAnalysisSpecialtyFilterCode,
            controller
        );
    }

    async loadProviderRosters(controller: AbortController) {
        try {
            await this.rosterStore.loadProviderRosters(controller);
        } catch (error) {
            // Silently handle abort errors - component unmounted or navigation occurred
            if (error instanceof Error && isAbortError(error)) {
                return;
            }
            throw error;
        }
    }

    async loadPatientRosters(controller: AbortController) {
        try {
            await this.patientStore.loadPatientRosters(controller);
        } catch (error) {
            // Silently handle abort errors - component unmounted or navigation occurred
            if (error instanceof Error && isAbortError(error)) {
                return;
            }
            throw error;
        }
    }

    // ============================================================================
    // Processing Files - Getters
    // ============================================================================

    get processingFiles(): ProcessingFile[] { return this.fileListStore.processingFiles; }
    get loadingProcessingFiles(): boolean { return this.fileListStore.loadingProcessingFiles; }
    get processingFilesPagination() { return this.fileListStore.processingFilesPagination; }
    get processingFilesTotalCount(): number { return this.fileListStore.totalProcessingFiles; }
    get uploadJobs(): UploadJob[] { return this.fileUploadStore.uploadJobs; }
    get hasActiveUploads(): boolean { return this.fileUploadStore.hasActiveUploads; }
    get activeUploadCount(): number { return this.fileUploadStore.activeUploadCount; }
    get isFileActionLoading(): boolean { return this.fileListStore.isFileActionLoading; }

    // SSE Connection delegation
    get processingDetailsSSEConnections() { return this.sseConnectionStore.processingDetailsSSEConnections; }

    // UI State delegation
    get showRevertModal(): boolean { return this.uiStateStore.showRevertModal; }
    set showRevertModal(value: boolean) { this.uiStateStore.setShowRevertModal(value); }
    get activeTab(): number { return this.uiStateStore.activeTab; }
    set activeTab(value: number) { this.uiStateStore.setActiveTab(value); }
    get renamingFileId(): string | null { return this.uiStateStore.renamingFileId; }
    set renamingFileId(value: string | null) { this.uiStateStore.setRenamingFileId(value); }
    get navItems() { return this.uiStateStore.navItems; }
    set navItems(value: NavItem[]) { this.uiStateStore.setNavItems(value); }

    // ============================================================================
    // Processing Files - Data Loading
    // ============================================================================

    async loadProcessingFiles(controller: AbortController, page = 1, pageSize = 20) {
        await this.fileListStore.loadProcessingFiles(controller, page, pageSize);
    }

    async loadFileDetail(fileId: number, controller: AbortController): Promise<{file: ProcessingFile; events: FileEvent[]} | null> {
        return await this.fileListStore.loadFileDetail(fileId, controller);
    }

    updateFileStatus(fileId: string, updates: Partial<ProcessingFile>) {
        this.fileListStore.updateFileStatus(fileId, updates);
    }

    // ============================================================================
    // Processing Files - SSE Real-time Updates
    // ============================================================================

    /**
     * Handle individual SSE event for file processing
     * Updates stages and activity directly from SSE events (no API refresh)
     *
     * @param fileId File upload ID
     * @param event SSE event data
     */
    private handleFileProcessingEvent(fileId: string, event: FileProcessingEvent) {
        this.fileListStore.handleFileProcessingEvent(fileId, event);
    }

    /**
     * Connect to processing details SSE stream for detailed per-file events
     * Called when processing details panel is opened
     *
     * CRITICAL: Only ONE active SSE connection allowed per file_id to prevent:
     * - Race conditions and duplicate message processing
     * - Missing messages (distributed across multiple streams)
     * - Resource leaks and connection buildup
     *
     * @param fileId File upload ID
     * @param fileName Display name for notifications
     * @param existingEvents Optional events from prior loadFileDetail call to avoid duplicate API request
     */
    async connectToProcessingDetailsStream(fileId: number, fileName: string, existingEvents?: FileEvent[]) {
        return this.sseConnectionStore.connectToProcessingDetailsStream(fileId, fileName, existingEvents);
    }

    disconnectProcessingDetailsStream(fileId: number) {
        this.sseConnectionStore.disconnectProcessingDetailsStream(fileId);
    }

    disconnectAllProcessingDetailsStreams() {
        this.sseConnectionStore.disconnectAllProcessingDetailsStreams();
    }

    stopFileListPolling() {
        this.fileListStore.stopFileListPolling();
    }

    disconnectAllFileStreams() {
        this.fileListStore.disconnectAllFileStreams();
    }

    startFileListPolling() {
        this.fileListStore.startFileListPolling();
    }


    // ============================================================================
    // Roster Management
    // ============================================================================

    async updateRoster(controller: AbortController) {
        if (!this.runId || !this.specialtyFilterCode || (this.selectedAdditionalProviderKeys.length === 0 && this.deselectedExistingProviderKeys.length === 0))
            return;

        // Delegate to RosterStore
        await this.rosterStore.updateRosterFromMetadata(
            this.runId,
            this.revisionId,
            this.specialtyFilterCode,
            this.selectedAdditionalProviderKeys,
            this.deselectedExistingProviderKeys,
            this.geographyFilters.county,
            this.appliedGeographyFilter.standard_type,
            this.appliedGeographyFilter.standard_version,
            this.geographyFilters.patient_roster_id,
            controller
        );
    }

    toggleRevision() {
        this.revisionId = this.revisionId ? 0 : 1;
    }

    calculateGeneralComplianceScores() {
        if (this.readyToLoadData() && this.complianceScores.length > 0 && (this.providerGeoJson && this.providerGeoJson?.features?.length > 0))
            return {
                scores: [
                    {name: '# of Provider Locations', value: this.providerGeoJson.features.length, compliant: null},
                    {name: 'Estimated Total Compliance', value: `${this.complianceScores.filter(cs => cs.compliance).length} / ${this.complianceScores.length}`, compliant: null}
                ]
            };
        return {scores: []};
    }

    // Setters for applied filters
    setGeographyFilter(filterName: 'state' | 'county' | 'standard_type' | 'patient_roster_name' | 'standard_version', value: string, resetSpecialty = true) {
        this.geographyStore.setGeographyFilter(filterName, value, resetSpecialty);
    }

    setDisruptionAnalysisFilter(filterName: 'state' | 'county' | 'standard_type' | 'patient_roster_name' | 'zip', value: string) {
        this.disruptionStore.setDisruptionAnalysisFilter(filterName, value);
    }

    resetGeographyFilter(preserve: Array<keyof AppliedGeographyFilter> = []) {
        this.geographyStore.resetGeographyFilter(preserve);
    }

    resetDisruptionAnalysisFilter() {
        this.disruptionStore.resetDisruptionAnalysisFilter();
    }

    setRevisionId(value: number) {
        this.revisionId = value;
    }

    setSpecialtyFilter(value: string) {
        this.geographyStore.setSpecialtyFilter(value);
    }

    setDisruptionAnalysisSpecialtyFilter(value: string) {
        this.disruptionStore.setDisruptionAnalysisSpecialtyFilter(value);
    } 

    setAdditionalProviderVisibleColumns(columns: string[]) {
        this.uiStateStore.setAdditionalProviderVisibleColumns(columns);
    }

    setAdditionalProviderPendingColumns(columns: string[]) {
        this.uiStateStore.setAdditionalProviderPendingColumns(columns);
    }

    // Download state getters for backward compatibility
    get isDownloading(): boolean { return this.fileListStore.isDownloading; }
    get downloadProgress(): number | null { return this.fileListStore.downloadProgress; }
    get downloadError(): string | null { return this.fileListStore.downloadError; }
    get downloadUsedFallback(): boolean { return this.fileListStore.downloadUsedFallback; }
    get cancelDownload(): (() => void) | null { return this.fileListStore.cancelDownload; }

    async downloadErrorReport(fileUploadId: number) {
        await this.fileListStore.downloadErrorReport(fileUploadId);
    }

    clearDownloadState() {
        this.fileListStore.clearDownloadState();
    }

    // Toast notification methods

    showToastNotification(
        kind: 'success' | 'error' | 'info' | 'warning',
        title: string,
        subtitle = '',
        options?: {errorId?: string; action?: {label: string; onClick: () => void}; timeout?: number}
    ) {
        this.notificationStore.showToastNotification(kind, title, subtitle, options);
    }


    hideToastNotification(id?: string) {
        if (id) {
            this.notificationStore.hideToastNotification(id);
        } else {
            // Legacy behavior: remove first notification
            const firstNotification = this.notificationStore.notifications[0];
            if (firstNotification) {
                this.notificationStore.hideToastNotification(firstNotification.id);
            }
        }
    }

    /**
     * Centralized error handler for API calls
     * Parses error, shows user-friendly toast, tracks analytics, and provides retry option
     *
     * @example
     * try {
     *     await api.copyRoster(...);
     * } catch (error) {
     *     this.handleApiError(
     *         error,
     *         'Failed to Copy Roster',
     *         () => this.copyRoster(...) // Retry callback
     *     );
     * }
     */
    handleApiError(error: unknown, title: string, retryCallback?: () => void) {
        const parsedError = parseApiError(error);

        // Silent abort - don't show error
        if (parsedError.isAbortError) return;

        // Show error toast with optional retry action
        this.showToastNotification(
            'error',
            title,
            parsedError.message,
            {
                errorId: parsedError.errorId,
                action: parsedError.retryable && retryCallback ? {
                    label: 'Retry',
                    onClick: retryCallback
                } : undefined
            }
        );
    }

    /**
     * Check if an error is an abort error (user cancelled request)
     * @param error - The caught error
     * @returns true if abort error, false otherwise
     */
    isAbortError(error: unknown): boolean {
        return error instanceof Error && error.name === 'AbortError';
    }

    /**
     * Store error for a specific domain (prevents cross-domain overwrites)
     * @param domain - The error domain (from ERROR_DOMAINS)
     * @param error - Parsed error object
     * @example setDomainError(ERROR_DOMAINS.ROSTERS, parsedError)
     */
    setDomainError(domain: ErrorDomain, error: ParsedApiError) {
        runInAction(() => {
            this.errorsByDomain.set(domain, error);
        });
    }

    /**
     * Get error for a specific domain
     * @param domain - The error domain (from ERROR_DOMAINS)
     * @returns Error object or undefined if no error for domain
     * @example const error = getDomainError(ERROR_DOMAINS.PROCESSING_FILES)
     */
    getDomainError(domain: ErrorDomain): ParsedApiError | undefined {
        return this.errorsByDomain.get(domain);
    }

    /**
     * Clear error for a specific domain
     * @param domain - The error domain (from ERROR_DOMAINS)
     * @example clearDomainError(ERROR_DOMAINS.ROSTERS)
     */
    clearDomainError(domain: ErrorDomain) {
        runInAction(() => {
            this.errorsByDomain.delete(domain);
        });
    }

    async revertProviderRosterToOriginal(controller: AbortController) {
        if (!this.runId || !this.specialtyFilterCode) return;
        await this.rosterStore.revertProviderRosterToOriginal(
            this.runId,
            this.revisionId,
            this.specialtyFilterCode,
            controller
        );
        this.revisionId = 0;
    }

    async copyRoster(rosterId: number, revisionName: string, notes: string, controller: AbortController) {
        return this.rosterStore.copyRoster(
            rosterId,
            revisionName,
            notes,
            controller,
            this.selectedAdditionalProviderKeys,
            this.deselectedExistingProviderKeys,
            this.runId ?? 0,
            this.specialtyFilterCode ?? '',
            this.geographyFilters.county,
            this.appliedGeographyFilter.standard_type,
            this.appliedGeographyFilter.standard_version,
            this.geographyFilters.patient_roster_id,
            (error, title, retryCallback) => this.handleApiError(error, title, retryCallback)
        );
    }

    async renameRoster(rosterId: number, revisionId: number, revisionName: string, notes: string | null, controller: AbortController) {
        return this.rosterStore.renameRoster(
            rosterId,
            revisionId,
            revisionName,
            notes,
            controller,
            (error, title, retryCallback) => this.handleApiError(error, title, retryCallback)
        );
    }

    async deleteRoster(rosterId: number, revisionId: number, controller: AbortController) {
        return this.rosterStore.deleteRoster(
            rosterId,
            revisionId,
            controller,
            (error, title, retryCallback) => this.handleApiError(error, title, retryCallback)
        );
    }

    async continueProcessing(fileId: string, controller: AbortController): Promise<void> {
        await this.fileListStore.continueProcessing(fileId, controller);
    }

    async renameFile(fileId: string, filename: string, controller: AbortController): Promise<string> {
        return await this.fileListStore.renameFile(fileId, filename, controller, (id) => this.renamingFileId = id);
    }

    async deleteFile(fileId: string, filename: string, controller: AbortController) {
        await this.fileListStore.deleteFile(fileId, filename, controller);
    }

    startResubmit(fileId: string, file: File): void {
        this.fileUploadStore.startResubmit(fileId, file, NetworkOptimizationStore.UPLOAD_CHUNK_SIZE);
    }


    get geographyFilters(): IGeographyFilterPayload { return this.geographyStore.getGeographyFiltersPayload(); }
    get disruptionAnalysisFilters(): IDisruptionAnalysisFilterPayload { return this.disruptionStore.getDisruptionAnalysisFiltersPayload(); }

    // Specialty filter stores codes directly (pipe-separated), so just return it
    get specialtyFilterCode(): string { return this.specialtyFilter; }
    get disruptionAnalysisSpecialtyFilterCode(): string { return this.disruptionAnalysisSpecialtyFilter; }

    get selectedSpecialtyCount(): number {
        if (!this.specialtyFilter) return 0;
        return this.specialtyFilter.split('|').filter(Boolean).length;
    }

    get selectedSpecialtyNames(): string[] {
        if (!this.specialtyFilter) return [];
        const codes = this.specialtyFilter.split('|').filter(Boolean);
        const scores = this.defaultComplianceScores.length > 0
            ? this.defaultComplianceScores
            : this.complianceScores;
        return codes.map((code: string) => {
            const match = scores.find((s: any) => s.specialtyCode === code);
            return match ? match.name : code;
        });
    }

    readyToLoadData() {
        const geographyReady = this.appliedGeographyFilter.standard_type && this.appliedGeographyFilter.standard_version;
        const disruptionReady = this.appliedDisruptionAnalysisFilter.standard_type && this.appliedDisruptionAnalysisFilter.patient_roster_name;
        return (geographyReady || disruptionReady) && this.runId;
    }

    selectAdditionalProviders(keys: string[]) {
        this.providerStore.selectAdditionalProviders(keys);
    }

    deselectAdditionalProviders(keys: string[]) {
        this.providerStore.deselectAdditionalProviders(keys);
    }

    deselectAllAdditionalProviders() {
        this.providerStore.deselectAllAdditionalProviders();
    }

    resetProviders() {
        this.providerStore.reset();
    }

    get selectedAdditionalProviders() {
        return this.localProviders.filter(p => this.selectedAdditionalProviderKeys.some(s => s === p['id']));
    }

    get nonSelectedAdditionalProviders() {
        return this.localProviders.filter(p => !this.selectedAdditionalProviderKeys.some(s => s === p['id']));
    }

    selectExistingProviders(keys: string[]) {
        this.providerStore.selectExistingProviders(keys);
    }

    selectAllExistingProviders() {
        this.providerStore.selectAllExistingProviders();
    }

    deselectExistingProviders(keys: string[]) {
        this.providerStore.deselectExistingProviders(keys);
    }

    deselectAllExistingProviders() {
        this.providerStore.deselectAllExistingProviders();
    }


    get multiSpecialtyProviderGeoJson(): Record<string, FetchGeoJsonResponse> | null {
        return this._multiSpecialtyProviderGeoJson;
    }

    get reportProviderGeoJson(): FetchGeoJsonResponse | null { return this.reportStore.reportProviderGeoJson; }
    get reportProviderGeoJsonError(): string | null { return this.reportStore.reportProviderGeoJsonError; }
    set reportProviderGeoJsonError(value: string | null) { this.reportStore.reportProviderGeoJsonError = value; }
    get reportProviderGeoJsonLoading(): boolean { return this.reportStore.reportProviderGeoJsonLoading; }
    set reportProviderGeoJsonLoading(value: boolean) { this.reportStore.reportProviderGeoJsonLoading = value; }
    get downloadingItems(): {[itemName: string]: boolean} { return this.reportStore.downloadingItems; }

    appendLocalProviders(providers: FetchProvidersResponse) {
        this.providerStore.appendLocalProviders(providers);
    }

    get distanceCalcType() { return this._distanceCalcType; }
    set distanceCalcType(value: string) { this._distanceCalcType = value; }
    get selectedRosterForComparison() { return this._selectedRosterForComparison; }
    set selectedRosterForComparison(value: string) { this._selectedRosterForComparison = value; }
    get selectedRosters(): Array<{rosterId: string, revisionId: string}> { return this.disruptionStore.selectedRosters; }
    set selectedRosters(value: Array<{rosterId: string, revisionId: string}>) { this.disruptionStore.selectedRosters = value; }
    get rosterComparisonStore() { return this._rosterComparisonStore; }
    get providerRosters() { return this.rosterStore.providerRosters; }
    set providerRosters(value: any[]) { this.rosterStore.providerRosters = value; }
    get loadingProviderRosters() { return this.rosterStore.loadingProviderRosters; }
    set loadingProviderRosters(value: boolean) { this.rosterStore.loadingProviderRosters = value; }
    get providerRostersStatus() { return this.rosterStore.providerRostersStatus; }
    get patientRosters() { return this.patientStore.patientRosters; }
    set patientRosters(value: any[]) {
        // Delegate to PatientStore's type-safe setter
        this.patientStore.patientRosters = value;
    }
    get loadingPatientRosters() { return this.patientStore.loadingPatientRosters; }

    filterProviderList(providerList: any[]) {
        let filteredList = providerList;

        this.filters.forEach(filter => {
            filteredList = applyFilter(filteredList, filter);
        });

        return filteredList;
    }

    async downloadReport(payload: IFetchReportsPayload, reportType: 'providers' | 'available-providers' | 'adequacy-scores' | 'standard-reporting', itemName: string) {
        await this.reportStore.downloadReport(
            payload,
            reportType,
            itemName,
            (error, title, retryCallback) => this.handleApiError(error, title, retryCallback)
        );
    }

    async downloadComplianceSummary(itemName: string, signal: AbortSignal) {
        if (!this.readyToLoadData()) return;
        if (!this.runId) {
            throw new Error('Cannot download compliance summary: runId not set');
        }
        await this.reportStore.downloadComplianceSummary(
            itemName,
            this.runId,
            this.revisionId,
            this.geographyFilters as IGeographyFilterPayload,
            this.distanceCalcType,
            this.appliedGeographyFilter,
            signal,
            (error, title, retryCallback) => this.handleApiError(error, title, retryCallback)
        );
    }

    // ── Chunked File Upload ────────────────────────────────────────────────────

    private static readonly UPLOAD_CHUNK_SIZE = 10 * 1024 * 1024;

    startFileUploads(files: File[]): void {
        this.fileUploadStore.startFileUploads(files);
    }

    clearCompletedJobs(): void {
        this.fileUploadStore.clearCompletedJobs();
    }

    removeJob(id: string): void {
        this.fileUploadStore.removeJob(id);
    }

    retryJob(id: string): void {
        this.fileUploadStore.retryJob(id);
    }

    cancelJob(id: string): void {
        this.fileUploadStore.cancelJob(id);
    }

    cancelAllJobs(): void {
        this.fileUploadStore.cancelAllJobs();
    }

    clearUploadJobs(): void {
        this.fileUploadStore.clearUploadJobs();
    }

    restoreUploadJobs(): void {
        this.fileUploadStore.restoreUploadJobs();
    }

}

